import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BiArrowBack } from 'react-icons/bi';
import { parivartanAPI } from '../../../services/parivartanAPI';
import WorkspaceDataTable from '../../../components/workspace/WorkspaceDataTable';
import WorkspacePageHeader from '../../../components/workspace/WorkspacePageHeader';
import { useAdminRecordDelete } from '../../../components/workspace/useAdminRecordDelete';
import { revisionColumn, useMasterRevision } from '../../../components/workspace/useMasterRevision';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { canManageMasters } from '../../../utils/permissions';
import './CustomerMaster.scss';

const SourceListPage = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useDocumentTitle('Sources');

  const load = useCallback(async () => {
    setError('');
    try {
      const response = await parivartanAPI.listSources();
      setRows(response.data?.data?.sources || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load sources');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      { key: 'sourceNo', label: 'Source no.', width: '150px', mobileTitle: true },
      { key: 'code', label: 'Code', width: '90px' },
      {
        key: 'name',
        label: 'Name',
        render: (value, row) => (
          <div className="cust-name">
            <span className="cust-name__title">{value}</span>
            {row.coverage ? <span className="cust-name__meta">{row.coverage}</span> : null}
          </div>
        )
      },
      { key: 'group', label: 'Group', width: '160px' },
      revisionColumn,
      {
        key: 'status',
        label: 'Status',
        width: '120px',
        render: (value) => (
          <span className={`cust-status cust-status--${value === 'active' ? 'active' : 'inactive'}`}>
            {value === 'active' ? 'Active' : 'Inactive'}
          </span>
        )
      }
    ],
    []
  );

  const admin = useAdminRecordDelete({
    entity: 'source',
    noun: 'source',
    plural: 'sources',
    reload: load,
    label: (row) => row.name
  });
  const revision = useMasterRevision({ entity: 'source' });

  if (!canManageMasters(user?.permissions, user?.roles)) {
    return <Navigate to="/apps/tender" replace />;
  }

  if (loading) return <Spinner animation="border" size="sm" />;

  return (
    <div className="cust-page">
      <button type="button" className="cust-page__back" onClick={() => navigate('/apps/tender/masters')}>
        <BiArrowBack />
        Masters
      </button>
      <WorkspacePageHeader
        title="Sources"
        help="sources"
        subtitle="Portals and channels where opportunities are found. Choose one of these when a tender is captured."
        actions={(
          <>
            <span className="cust-page__count">Total records {rows.length}</span>
            <Button size="sm" variant="primary" onClick={() => navigate('/apps/tender/masters/sources/new')}>
              New
            </Button>
          </>
        )}
      />
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {admin.notice}
      <WorkspaceDataTable
        downloadPermission="masters.download"
        title="Sources"
        data={rows}
        columns={columns}
        searchPlaceholder="Search number, code, name, group…"
        pageSize={12}
        onView={(row) => navigate(`/apps/tender/masters/sources/${row._id}`)}
        onEdit={(row) => navigate(`/apps/tender/masters/sources/${row._id}/edit`)}
        {...admin.tableProps}
        rowActions={revision.wrapActions(admin.wrapActions(() => [
          { type: 'view', label: 'View' },
          { type: 'edit', label: 'Edit' },
          { type: 'delete', label: 'Delete', variant: 'danger' }
        ]))}
      />
      {admin.modal}
      {revision.modals}
    </div>
  );
};

export default SourceListPage;
